import type { FieldDefinition, PersonConfig } from "~/models/Automation";
import { type MaybeRefOrGetter, type Ref } from "vue";
import { object } from "yup";
import ApiError from "~/plugins/errors/apiError";
import type { Notification } from "#ui/types";
import { v4 as uuidv7, validate, type Version4Options } from "uuid";

export function makeVariable(
    text: string | null | undefined,
    prefix: string = "{{",
    suffix: string = "}}",
) {
    if (!text) {
        return "";
    }

    // Trim the prefix and suffix from the text initially
    text = text
        .toString()
        .trim()
        .replace(new RegExp(`^${prefix}`), "")
        .replace(new RegExp(`${suffix}$`), "");

    return `${prefix}${text}${suffix}`;
}

export function trimPeriods(str: string): string {
    return str.replace(/^\.+|\.+$/g, "");
}

export function keyPrefix(prefix: string, key: string): string {
    const trimmedPrefix = trimPeriods(prefix);
    const trimmedKey = trimPeriods(key);

    // Join the trimmed parts with a single dot
    const joined = [trimmedPrefix, trimmedKey].join(".");

    // Trim potential leading/trailing dots resulting from joining empty trimmed parts
    return trimPeriods(joined);
}

export function isValidUuid(uuid: string) {
    return validate(uuid);
}

export function dateFormatOptions() {
    return Intl.DateTimeFormat().resolvedOptions();
}

export function apiRoute(
    name: string,
    expected: string,
    params?: Record<string, any>,
) {
    return params && Object.entries(params)
        ? expected.replace(/{(\w+)}/g, (_, key) => params[key] || "")
        : expected;
}

export function period(type: string, count: number) {
    return [
        { value: "day", label: count > 1 ? "days" : "day" },
        { value: "week", label: count > 1 ? "weeks" : "week" },
        { value: "month", label: count > 1 ? "months" : "month" },
        { value: "quarter", label: count > 1 ? "quarters" : "quarter" },
        { value: "year", label: count > 1 ? "years" : "year" },
    ].find(({ value }) => value === type)?.label;
}

export function convertToShortNumber(
    number: string | number,
    locale = "en-US",
) {
    const tiers = { k: 1000, m: 1000000, b: 1000000000 };

    if (typeof number === "string") {
        number = number.replaceAll(",", "");
        number = number.includes(".") ? parseFloat(number) : parseInt(number);
    }

    if (!number) {
        return number;
    }

    let tier = Object.keys(tiers)
        .reverse()
        .find(
            (tier) => (number as number) >= tiers[tier as keyof typeof tiers],
        );
    if (!tier) return new Intl.NumberFormat(locale).format(number as number);

    const value = (number as number) / tiers[tier as keyof typeof tiers];

    return (
        new Intl.NumberFormat(locale, { maximumSignificantDigits: 3 }).format(
            value,
        ) + tier.toUpperCase()
    );
}

export function beforeAfter(beforeAfter: string) {
    return [
        { label: "before", value: "last" },
        { label: "after", value: "next" },
    ].find(({ value }) => value === beforeAfter)?.label;
}

export function dateFormatOption(
    key: keyof Intl.ResolvedDateTimeFormatOptions,
) {
    return Intl.DateTimeFormat().resolvedOptions()[
        key
    ] as Intl.ResolvedDateTimeFormatOptions[typeof key];
}

export function createDottedObject(object: any) {
    return Object.keys(object ?? {}).reduce((acc, key) => {
        if (object[key] && typeof object[key] === "object") {
            const flattenedObject = createDottedObject(object[key]);
            Object.keys(flattenedObject).forEach((flattenedKey) => {
                acc[`${key}.${flattenedKey}`] = flattenedObject[flattenedKey];
            });
        } else {
            acc[key] = object[key];
        }
        return acc;
    }, {});
}

export function createUndottedObject(
    object: Record<string | number, any>,
): Record<string | number, any> {
    object = useCloneDeep(object ?? {});
    const result: Record<string, any> = {};
    const store: Record<string, any> = {};

    for (let key in object) {
        key.split(".").reduce(
            (
                result: Record<string | number, any>,
                currentValue,
                currentIndex,
                arr,
            ) => {
                if (typeof result !== "object") {
                    store[key] = object[key];

                    return result;
                }

                result[currentValue] =
                    arr.length - 1 === currentIndex
                        ? object[key]
                        : result[currentValue] || {};

                return result[currentValue];
            },
            result,
        );
    }

    return Object.keys(store).length
        ? useMergeWith(
              result,
              createUndottedObject(store),
              function (value, srcValue, key) {
                  if (
                      typeof value !== "object" &&
                      typeof srcValue === "object"
                  ) {
                      if (
                          Array.isArray(srcValue) &&
                          !srcValue.includes(value)
                      ) {
                          srcValue.push(value);
                          return srcValue;
                      }

                      return {
                          [key]: value,
                          ...srcValue,
                      };
                  }

                  return srcValue;
              },
          )
        : result;
}

export function range(low: number, high: number, step: number = 1) {
    const matrix = [];
    let initialValue;
    let finalValue;

    const walker = step || 1;
    let chars = false;
    if (!isNaN(low) && !isNaN(high)) {
        initialValue = low;
        finalValue = high;
    } else if (isNaN(low) && isNaN(high)) {
        chars = true;
        initialValue = low.toString().charCodeAt(0);
        finalValue = high.toString().charCodeAt(0);
    } else {
        initialValue = isNaN(low) ? 0 : low;
        finalValue = isNaN(high) ? 0 : high;
    }
    const plus = !(initialValue > finalValue);
    if (plus) {
        while (initialValue <= finalValue) {
            matrix.push(
                chars ? String.fromCharCode(initialValue) : initialValue,
            );
            initialValue += walker;
        }
    } else {
        while (initialValue >= finalValue) {
            matrix.push(
                chars ? String.fromCharCode(initialValue) : initialValue,
            );
            initialValue -= walker;
        }
    }
    return matrix;
}

export enum FieldMapType {
    DataKey,
    DataKeyAsValue,
    Default,
    Label,
}

export type SimpleFieldDefinition<TMetadata = any> = Partial<
    FieldDefinition<any, TMetadata>
> &
    Pick<FieldDefinition<any, TMetadata>, "key" | "type" | "default_value">;

export class FieldCollection {
    public items: FieldDefinition[];

    constructor(items: FieldDefinition[] = []) {
        this.items = useCloneDeep(items) || [];
    }

    static match(text: string, prefix: string, suffix: string) {
        function escapeRegExp(text: string) {
            return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
        }

        const regex = new RegExp(
            `${escapeRegExp(prefix)}(.*?)${escapeRegExp(suffix)}`,
            "g",
        );

        return Array.from(text.matchAll(regex));
    }

    getKeyValueMap(
        options?: Partial<{
            dataKey: boolean;
            dataKeyAsValue: boolean;
            asOptionLabel: boolean;
            forValidation: boolean;
            label: boolean;
        }>,
    ) {
        const {
            dataKey = false,
            dataKeyAsValue = false,
            asOptionLabel = false,
            forValidation = false,
            label = false,
        } = options ?? {};
        const map: Record<string, any> = {};

        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const key = dataKey ? item.data_key : item.key;

            let value;

            if (dataKeyAsValue) {
                value = item.data_key;
            } else if (forValidation) {
                value = item.validation_value || item.default_value;
            } else if (label) {
                value = item.label;
            } else {
                value = item.default_value;
            }

            if (asOptionLabel && item.options?.length) {
                if (Array.isArray(value)) {
                    value = value.map((val: any) => {
                        const opt = item.options.find(
                            (opt) => opt.value === val,
                        );
                        return opt ? opt.label : val;
                    });
                } else if (value) {
                    const opt = item.options.find((opt) => opt.value === value);
                    value = opt ? opt.label : value;
                }
            }

            if (key in map) {
                map[key] = map[key] || value;
            } else {
                map[key] = value;
            }

            if (!dataKey && item.aliases) {
                for (let j = 0; j < item.aliases.length; j++) {
                    const alias = item.aliases[j];
                    map[alias] = value;
                }
            }
        }

        return map;
    }

    findMatches(
        text: string | string[],
        options: Partial<{
            type: FieldMapType;
            prefix: string;
            suffix: string;
        }> = {},
    ) {
        if (typeof text === "string") text = [text];
        options = {
            type: FieldMapType.Default,
            prefix: "{{",
            suffix: "}}",
            ...options,
        };

        const matches = [...text];

        text.forEach((txt) => {
            const match = FieldCollection.match(
                txt,
                options.prefix as string,
                options.suffix as string,
            ).map((match) => match[1]);

            matches.push(...match);
        });

        return createFieldCollection(
            useArrayUnique(matches)
                .value.map((value) => {
                    if (FieldMapType.DataKey === options.type) {
                        return this.firstWhere("data_key", value);
                    } else {
                        return this.firstWhere(
                            (field) =>
                                field.key === value ||
                                (field.aliases ?? []).includes(value as string),
                        );
                    }
                })
                .filter((value) => !!value) as FieldDefinition[],
        );
    }

    replaceContent(
        text: any,
        options: Partial<{
            except: string[];
            only: string[];
            parent: string | null;
            type: FieldMapType;
            forValidation: boolean;
            showOptionLabel: boolean;
            nullOnEmpty: boolean;
            labelAsValue: boolean;
            replaceWhenEmpty: boolean;
            prefix: string;
            suffix: string;
            fallback: any;
        }> = {},
    ) {
        if (!text) {
            return text;
        }

        const initialText = text;

        options = {
            except: [],
            only: [],
            parent: null,
            type: FieldMapType.Default,
            forValidation: false,
            nullOnEmpty: false,
            showOptionLabel: false,
            replaceWhenEmpty: true,
            prefix: "{{",
            suffix: "}}",
            ...options,
        };

        if (Array.isArray(text) || typeof text === "object") {
            for (const key in text) {
                const parentKey = options.parent
                    ? `${options.parent}.${key}`
                    : key;

                if (
                    options.except?.includes(parentKey) ||
                    (options?.only?.length &&
                        !options.only?.includes(parentKey))
                ) {
                    continue;
                }

                if (
                    typeof text[key] === "string" ||
                    Array.isArray(text[key]) ||
                    typeof text[key] === "object"
                ) {
                    text[key] = this.replaceContent(text[key], {
                        ...options,
                        parent: parentKey,
                    });
                }
            }
        }

        if (typeof text !== "string") {
            return text;
        }

        const values: FieldDefinition[] = [
            text,
            ...FieldCollection.match(
                text,
                options.prefix as string,
                options.suffix as string,
            ).map((match) => match[1]),
        ]
            .map((value) => {
                if (FieldMapType.DataKey === options.type) {
                    return this.firstWhere("data_key", value);
                } else {
                    return this.firstWhere(
                        (field) =>
                            field.key === value ||
                            (field.aliases ?? []).includes(value),
                    );
                }
            })
            .filter((value) => !!value) as FieldDefinition[];

        const map = new FieldCollection(values);

        let valueMap;

        if (options.showOptionLabel) {
            valueMap = map.getKeyValueMap({
                asOptionLabel: true,
            });
        } else if (options.type === FieldMapType.DataKey) {
            valueMap = map.getKeyValueMap({
                dataKey: true,
                forValidation: options.forValidation,
            });
        } else if (options.type === FieldMapType.Label) {
            valueMap = map.getKeyValueMap({
                label: true,
                forValidation: options.forValidation,
            });
        } else if (options.type === FieldMapType.DataKeyAsValue) {
            valueMap = map.getKeyValueMap({
                dataKeyAsValue: true,
                forValidation: options.forValidation,
            });
        } else {
            valueMap = map.getKeyValueMap({
                forValidation: options.forValidation,
            });
        }

        if (text in valueMap) {
            return valueMap[text] ?? "";
        }

        for (const key of Object.keys(valueMap)) {
            if (options.replaceWhenEmpty || valueMap[key]) {
                text = text
                    .replaceAll(
                        `${options.prefix}${key}${options.suffix}`,
                        valueMap[key] ?? "",
                    )
                    .replaceAll(
                        `${options.prefix} ${key} ${options.suffix}`,
                        valueMap[key] ?? "",
                    )
                    .replaceAll(
                        `${options.prefix} ${key}${options.suffix}`,
                        valueMap[key] ?? "",
                    )
                    .replaceAll(
                        `${options.prefix}${key} ${options.suffix}`,
                        valueMap[key] ?? "",
                    );
            }
        }

        if (!text && options.fallback) {
            return options.fallback;
        }

        return text === initialText && options.nullOnEmpty ? null : text.trim();
    }

    firstWhere(
        key: keyof FieldDefinition | ((item: FieldDefinition) => boolean),
        value?: any,
    ) {
        return this.items.find((item) =>
            typeof key === "function" ? key(item) : item[key] === value,
        );
    }

    getExternalId() {
        return this.firstWhere("is_external_id", true);
    }

    withData(data: Record<string, any>, ignoreEmpty: boolean = false) {
        this.items = (this.items || []).map((item) => {
            if (item.key in data && (!ignoreEmpty || data[item.key])) {
                item.default_value = data[item.key];
            } else if (useHas(data, item.key)) {
                item.default_value = useGet(data, item.key);
            }

            for (const alias of item.aliases ?? []) {
                if (alias in data && (!ignoreEmpty || data[alias])) {
                    item.default_value = data[alias];
                } else if (useHas(data, alias)) {
                    item.default_value = useGet(data, alias);
                }
            }

            return item;
        });

        return this;
    }

    all(): FieldDefinition[] {
        return this.items;
    }

    add(...field: FieldDefinition[]) {
        field.forEach((item) => {
            if (!item.label) {
                item.label = item.key;
            }

            if (!item.data_key) {
                item.data_key = item.key;
            }

            if (!item.attribute) {
                item.attribute = item.key;
            }

            const index = this.items.findIndex(
                (existing) => existing.key === item.key,
            );
            if (index >= 0) {
                this.items.splice(index, 1, item);
            } else {
                this.items.push(item);
            }
        });

        return this;
    }

    addSimple<TMetadata = Record<string, unknown> | null>(
        ...field: SimpleFieldDefinition[]
    ) {
        return this.add(...(field as FieldDefinition<any, TMetadata>[]));
    }
}

export function createFieldCollection(
    fields: FieldDefinition[] | FieldCollection = [],
) {
    return fields instanceof FieldCollection
        ? fields
        : new FieldCollection(fields);
}

export function createSimpleFieldCollection(fields: SimpleFieldDefinition[]) {
    return createFieldCollection().addSimple(...fields);
}

type UiAvatarParams = {
    size?: number;
    fontSize?: number;
    length?: number;
    name?: string;
    rounded?: boolean;
    bold?: boolean;
    uppercase?: boolean;
    format?: "png" | "svg";
    background?: string;
    color?: string;
};

const createUiAvatar = (params: UiAvatarParams = {}): string => {
    function removeHash(value?: string): string {
        return value?.replace("#", "") || "";
    }

    const size = params.size && Math.min(Math.max(16, params.size), 512);
    const fontSize =
        params.fontSize &&
        Math.round(Math.min(Math.max(0.1, params.fontSize), 1));
    const length = params.length && Math.min(Math.max(1, params.length), 4);
    const name = params.name;
    const rounded = params.rounded;
    const bold = params.bold;
    const uppercase = params.uppercase;
    const format = params.format;
    const background = params.background && removeHash(params.background);
    const color = params.color && removeHash(params.color);

    const queryParams: UiAvatarParams = {
        size,
        fontSize,
        length,
        name,
        rounded,
        bold,
        uppercase,
        format,
        background,
        color,
    };

    const url = new URL("https://ui-avatars.com/api/");

    Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
            url.searchParams.append(key, String(value));
        }
    });

    return url.toString();
};

export function useChanges<T extends Record<string, any>>(
    value: MaybeRefOrGetter<T>,
) {
    const state: Ref<T> = toRef(value) as unknown as Ref<T>;
    const hasChanges = ref<boolean>(false);
    const changes = ref<Partial<T>>({});
    const initial = ref(useCloneDeep(state.value));

    function getChangedValues() {
        const changedValues: any = {};

        for (const key in initial.value) {
            if (initial.value[key] !== state.value[key]) {
                changedValues[key] = state.value[key];
            }
        }

        return changedValues;
    }

    watch(
        state,
        () => {
            const changedValues = getChangedValues();

            if (Object.keys(changedValues).length > 0) {
                hasChanges.value = true;
            } else {
                hasChanges.value = false;
            }

            changes.value = changedValues;
        },
        {
            deep: true,
        },
    );

    return {
        initial,
        state,
        changes,
        hasChanges,
        reset: () => {
            state.value = useCloneDeep(initial.value) as T;
        },
    };
}

interface HireDataForm<T> {
    toast?: Partial<Notification> | ((state: T) => Partial<Notification>);
    spec: Parameters<typeof object>[0];
    state: MaybeRefOrGetter<T>;
    handleSubmit: () => Promise<any>;
    onCommit?: () => void;
    onReset?: () => void;
    handleError?: (error: ApiError<T> | Error) => void;
}

export function useHireDataForm<T>(options: HireDataForm<T>): {
    state: Ref<T>;
    schema: ReturnType<typeof object>;
    submitting: Ref<boolean>;
    errors: Ref<ApiError | undefined>;
    handleSubmit: () => Promise<void>;
} {
    const initialState = toRef(options.state) as Ref<T>;
    const innerState = toRef(useCloneDeep(initialState.value)) as Ref<T>;
    const resetting = ref<boolean>(false);
    const submitting = ref<boolean>(false);
    const errors = ref<ApiError<T> | undefined>();

    function commit() {
        initialState.value = useCloneDeep(innerState.value);

        if (options.onCommit) options.onCommit();
    }

    function reset() {
        resetting.value = true;
        innerState.value = useCloneDeep(initialState.value);

        if (options.onReset) options.onReset();
    }

    async function handleSubmit() {
        submitting.value = true;
        errors.value = undefined;

        await options
            .handleSubmit()
            .then(() => {
                if (!resetting.value && options.toast)
                    if (typeof options.toast === "function") {
                        const toast = options.toast(innerState.value);
                        useToast().add({
                            ...toast,
                            actions: toast.actions ?? [
                                {
                                    label: "Undo",
                                    click: () => {
                                        reset();
                                        handleSubmit();
                                    },
                                },
                            ],
                            callback: () => {
                                if (toast.callback) toast.callback();

                                commit();
                            },
                        });
                    } else {
                        useToast().add({
                            ...options.toast,
                            actions: options.toast.actions ?? [
                            {
                                label: "Undo",
                                click: () => {
                                    reset();
                                    handleSubmit();
                                },
                            },
                        ],
                        callback: () => {
                            if (options.toast.callback)
                                options.toast.callback();

                            commit();
                        },
                        });
                    }
            })
            .catch((error: ApiError<T> | Error) => {
                if (error instanceof ApiError) {
                    errors.value = error;
                }

                if (options.handleError)
                    options.handleError(error as ApiError<T> | Error);
            })
            .finally(() => {
                submitting.value = false;
                resetting.value = false;
            });
    }

    return {
        state: innerState as Ref<T>,
        schema: object(options.spec),
        submitting,
        errors,
        handleSubmit,
    };
}

export function createUuid(
    options?: Version4Options,
    buf?: undefined,
    offset?: number,
): string {
    return uuidv7(options, buf, offset);
}

export function route(expected: string, params?: Record<string, any>) {
    return expected.replace(/{(\w+)}/g, (_, key) => params?.[key] || "");
}

export function createPersonConfig(
    mergeObject?: Partial<PersonConfig>,
): PersonConfig {
    let config = {
        uuid: createUuid(),
        title: null,
        initials: null,
        first_name: null,
        label: null,
        prefix: null,
        last_name: null,
        primary_email: null,
        primary_phone_number: null,
        language: null,
        user: null,
        name: null,
        birth_date: null,
        gdpr_expires_at: null,
        is_subscribed: true,
        address: {
            address1: null,
            address2: null,
            city: null,
            province: null,
            zip: null,
        },
        utm_campaign: {
            source: null,
            medium: null,
            campaign: null,
            term: null,
            content: null,
        },
        source: {
            auto_sync: true,
            app: null,
            domain: null,
            url: null,
            external_id: null,
            external_type: null,
        },
        candidate: {
            availability: null,
            availability_date: null,
            employment_type: null,
            notice_period_type: null,
            notice_period_count: null,
            notice_period_end_date: null,
            salary_expectation: null,
            application_source: null,
            application_status: null,
            preferred_contact_method: null,
            willing_to_relocate: null,
            remote_work_preference: null,
            skills: [undefined],
            certificates: [{ name: undefined, url: undefined }],
            auto_sync_skills: true,
            auto_sync_certificates: true,
        },
        tags: [],
        custom_fields: [],
        language_config: {
            code: null,
            name: null,
            ISO_639_1: null,
            ISO_639_2T: null,
            ISO_639_2B: null,
            scope: null,
            type: null,
            additional: null,
        },
        locale: {
            code: null,
            name: null,
        },
        country: {
            code: null,
            name: null,
            alpha_2: null,
            alpha_3: null,
            iso_3166_2: null,
            region: null,
            sub_region: null,
            intermediate_region: null,
            region_code: null,
            sub_region_code: null,
            intermediate_region_code: null,
            additional: null,
        },
    } as PersonConfig;

    if (mergeObject) {
        const dottedConfig = createDottedObject(config);
        const dottedMergeObject = createDottedObject(mergeObject);
        config = createUndottedObject(
            Object.assign(dottedConfig, dottedMergeObject),
        ) as PersonConfig;
    }

    return config;
}

function useAppLogoUrl(options?: {
    type?: "icon" | "text-horizontal" | "text-vertical" | "text";
    grayscale?: boolean;
    extension?: "png" | "svg";
}) {
    const type = options?.type ?? "text-horizontal";
    const grayscale = options?.grayscale ?? false;
    const extension = options?.extension ?? "svg";
    let variant;

    if (type === "icon") {
        variant = grayscale ? "logo_gray" : "logo_color";
    } else if (type === "text-horizontal") {
        variant = grayscale
            ? "logo_text_gray_horizontal"
            : "logo_text_color_horizontal";
    } else if (type === "text-vertical") {
        variant = grayscale
            ? "logo_text_gray_vertical"
            : "logo_text_color_vertical";
    } else {
        variant = "text_gray";
    }

    return `/assets/logo/${extension}/${variant}.${extension}`;
}

export { createUiAvatar, useAppLogoUrl };
